import { useEffect, useState } from 'react'
import { convertMarkdownToHtml } from '@/content-viewer/util/markdown-to-html'
import { fetchAgencyDocument, fetchAgencyOriginal, type AgencyDocument, type AgencyPdfHighlights } from '../fetch/agency-document'
import { AgencyOriginalFormat, getAgencyOriginalFormat } from '../utils/agency-original-format'
import { decodeAgencyTextOriginal, plainTextToHtml } from '../utils/agency-text-original'

/** What the agency viewer renders; the matter hook and the matter-free page's hook both return it. */
export type AgencyDocumentViewerState = {
  data: AgencyDocument | null
  file: File | null
  pdfHighlights: AgencyPdfHighlights
  html: string | null
  error: boolean
  /** The document is gone for good, so the viewer offers no Retry. */
  notFound: boolean
  originalError: boolean
  retry: () => void
}

export function useAgencyDocument(conversationId: string | undefined, referenceId: string, tableId?: string): AgencyDocumentViewerState {
  const [data, setData] = useState<AgencyDocument | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [pdfHighlights, setPdfHighlights] = useState<AgencyPdfHighlights>([])
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [originalError, setOriginalError] = useState(false)
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    const controller = new AbortController()
    setData(null)
    setFile(null)
    setPdfHighlights([])
    setHtml(null)
    setError(false)
    setOriginalError(false)
    async function load() {
      try {
        const document = await fetchAgencyDocument(conversationId, referenceId, controller.signal, tableId)
        if (controller.signal.aborted) return
        setData(document)
        // Only a viewable stored original is fetched; every other format shows the saved evidence alone.
        const format = getAgencyOriginalFormat(document.media_type)
        if (!format || !document.original_available) return
        try {
          if (format === AgencyOriginalFormat.PDF) {
            const original = await fetchAgencyOriginal(conversationId, referenceId, controller.signal, true, tableId)
            if (controller.signal.aborted) return
            setPdfHighlights(original.pdfHighlights)
            setFile(new File([original.blob], original.fileName || 'decision.pdf', { type: 'application/pdf' }))
            return
          }
          const original = await fetchAgencyOriginal(conversationId, referenceId, controller.signal, false, tableId)
          const text = decodeAgencyTextOriginal(await original.blob.arrayBuffer())
          const nextHtml = format === AgencyOriginalFormat.MARKDOWN ? await convertMarkdownToHtml(text) : plainTextToHtml(text)
          if (controller.signal.aborted) return
          setHtml(nextHtml)
        } catch {
          if (!controller.signal.aborted) setOriginalError(true)
        }
      } catch {
        if (!controller.signal.aborted) setError(true)
      }
    }
    void load()
    return () => controller.abort()
  }, [conversationId, referenceId, tableId, attempt])
  // A reference read keeps the retryable error; only a document opened by ID can leave the index.
  return { data, file, pdfHighlights, html, error, notFound: false, originalError, retry: () => setAttempt((value) => value + 1) }
}
